import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, User, ArrowLeft, X, AlertCircle, CheckCircle, LayoutDashboard, ShoppingBag } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { SESSAO_EXPIRADA_KEY } from '../services/api';

// ── Helpers: Usuários Recentes ──────────────────────────────────────────────
const MAX_RECENTS = 5;
const RECENTS_KEY = 'client_recent_users';

const getInitials = (name: string): string => {
  if (!name) return '?';
  const parts = name.split(/[\s._@-]/);
  return parts.slice(0, 2).map((p: string) => p[0]?.toUpperCase()).join('') || name[0].toUpperCase();
};

const avatarColors = [
  '#EA580C', '#DC2626', '#7C3AED', '#2563EB', '#059669',
  '#D97706', '#0891B2', '#BE185D', '#65A30D', '#C026D3',
];

const getAvatarColor = (str: string): string => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash);
  return avatarColors[Math.abs(hash) % avatarColors.length];
};
// ────────────────────────────────────────────────────────────────────────────

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [senha, setSenha] = useState('');
  const [mostrarSenha, setMostrarSenha] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [erroConexao, setErroConexao] = useState(false);
  const [step, setStep] = useState<'recent' | 'form' | 'password'>('recent');
  const [recentUsers, setRecentUsers] = useState<Array<{ email: string; nome: string }>>([]);
  const [selectedUser, setSelectedUser] = useState<{ email: string; nome: string } | null>(null);
  const [alertModal, setAlertModal] = useState<{
    show: boolean;
    type: 'error' | 'success' | 'warning';
    title: string;
    message: string;
    persistent?: boolean;
  }>({
    show: false,
    type: 'error',
    title: '',
    message: ''
  });
  const [showEscolhaDestino, setShowEscolhaDestino] = useState(false);
  const [usuarioLogado, setUsuarioLogado] = useState<any>(null);

  const { login, usuario } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const aviso = sessionStorage.getItem(SESSAO_EXPIRADA_KEY);
    if (aviso) {
      sessionStorage.removeItem(SESSAO_EXPIRADA_KEY);
      toast.error(aviso, { id: 'sessao-expirada', duration: 6000 });
    }
  }, []);
  const location = useLocation();
  const { empresaId } = useParams<{ empresaId?: string }>();

  // Redirecionar para a página que o usuário tentou acessar ou para o dashboard se for admin
  const from = location.state?.from?.pathname || '/dashboard';

  // Carregar usuários recentes
  useEffect(() => {
    try {
      const saved = localStorage.getItem(RECENTS_KEY);
      if (saved) {
        const users = JSON.parse(saved);
        setRecentUsers(users);
        if (users.length === 0) setStep('form');
      } else {
        setStep('form');
      }
    } catch {
      setStep('form');
    }
  }, []);

  // Função para mostrar alertas modais
  const showAlert = (type: 'error' | 'success' | 'warning', title: string, message: string, persistent: boolean = false) => {
    setAlertModal({
      show: true,
      type,
      title,
      message,
      persistent
    });
  };

  // Função para fechar alerta
  const closeAlert = () => {
    setAlertModal(prev => ({ ...prev, show: false }));
  };

  const saveRecentUser = (userInfo: { email: string; nome: string }) => {
    try {
      const existing: Array<{ email: string; nome: string }> = JSON.parse(localStorage.getItem(RECENTS_KEY) || '[]');
      const filtered = existing.filter(u => u.email !== userInfo.email);
      const updated = [userInfo, ...filtered].slice(0, MAX_RECENTS);
      localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
      setRecentUsers(updated);
    } catch { /* ignore */ }
  };

  const removeRecent = (e: React.MouseEvent, userEmail: string) => {
    e.stopPropagation();
    const updated = recentUsers.filter(u => u.email !== userEmail);
    localStorage.setItem(RECENTS_KEY, JSON.stringify(updated));
    setRecentUsers(updated);
    if (updated.length === 0) setStep('form');
  };

  const handleSelectRecent = (user: { email: string; nome: string }) => {
    setSelectedUser(user);
    setEmail(user.email);
    setSenha('');
    setErroConexao(false);
    setStep('password');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !senha) {
      showAlert('warning', 'Campos Obrigatórios', 'Preencha todos os campos para continuar.');
      return;
    }

    // Validação básica de email
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      showAlert('warning', 'Email Inválido', 'Por favor, insira um email válido.');
      return;
    }

    setIsLoading(true);
    setErroConexao(false);

    try {
      await login(email, senha, false, true);

      // Aguardar um pouco para o contexto atualizar
      setTimeout(() => {
        const usuarioSalvo = localStorage.getItem('authUsuario');
        const usuarioAtual = usuarioSalvo ? JSON.parse(usuarioSalvo) : null;

        if (!usuarioAtual) {
          if (empresaId) {
            navigate(`/menu/${empresaId}`);
          } else {
            navigate('/');
          }
          return;
        }

        // Salvar nos usuários recentes
        saveRecentUser({ email, nome: usuarioAtual.nome || email.split('@')[0] });

        // Verificar se tem role (gestao, colaborador, caixa, admin) ou nivel diferente de 'usuario'
        const temCargo = (usuarioAtual.role &&
          (usuarioAtual.role === 'gestao' ||
            usuarioAtual.role === 'colaborador' ||
            usuarioAtual.role === 'caixa' ||
            usuarioAtual.role === 'admin')) ||
          (usuarioAtual.nivel && usuarioAtual.nivel !== 'usuario');

        if (temCargo) {
          // Usuário com cargo - mostrar modal de escolha
          setUsuarioLogado(usuarioAtual);
          setShowEscolhaDestino(true);
        } else {
          // Cliente normal - redirecionar para o menu
          showAlert('success', 'Login Realizado!', 'Login realizado com sucesso! Redirecionando...');
          setTimeout(() => {
            if (empresaId) {
              navigate(`/menu/${empresaId}`);
            } else {
              navigate('/');
            }
          }, 1500);
        }
      }, 100); // Aguardar 100ms para o contexto atualizar
    } catch (error: any) {
      // Tratamento específico de erros baseado no status e mensagem
      let errorTitle = 'Erro no Login';
      let errorMessage = 'Erro ao fazer login';
      let isConnectionError = false;
      let isPersistent = false;

      if (error.message) {
        if (error.message.includes('Email ou senha incorretos')) {
          errorTitle = 'Credenciais Incorretas';
          errorMessage = 'Email ou senha incorretos. Verifique suas credenciais e tente novamente.';
          isPersistent = true; // Erro de senha deve ser persistente
        } else if (error.message.includes('Email e senha são obrigatórios')) {
          errorTitle = 'Campos Obrigatórios';
          errorMessage = 'Por favor, preencha todos os campos.';
        } else if (error.message.includes('Erro interno do servidor')) {
          errorTitle = 'Servidor Indisponível';
          errorMessage = 'Servidor temporariamente indisponível. Tente novamente em alguns minutos.';
          isConnectionError = true;
        } else if (error.message.includes('Sem conexão com o servidor') ||
          error.message.includes('Network Error') ||
          error.message.includes('ERR_NETWORK')) {
          errorTitle = 'Sem Conexão';
          errorMessage = 'Sem conexão com a internet. Verifique sua conexão e tente novamente.';
          isConnectionError = true;
          isPersistent = true;
        } else if (error.message.includes('timeout')) {
          errorTitle = 'Tempo Esgotado';
          errorMessage = 'Tempo limite esgotado. Verifique sua conexão e tente novamente.';
          isConnectionError = true;
        } else if (error.message.includes('404')) {
          errorTitle = 'Serviço Não Encontrado';
          errorMessage = 'Serviço não encontrado. Entre em contato com o suporte.';
        } else if (error.message.includes('500')) {
          errorTitle = 'Erro do Servidor';
          errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
          isConnectionError = true;
        } else {
          errorMessage = error.message;
        }
      } else if (error.response) {
        // Tratar erros de resposta HTTP
        switch (error.response.status) {
          case 400:
            errorTitle = 'Dados Inválidos';
            errorMessage = 'Dados inválidos. Verifique os campos preenchidos.';
            break;
          case 401:
            errorTitle = 'Credenciais Incorretas';
            errorMessage = 'Email ou senha incorretos. Verifique suas credenciais e tente novamente.';
            isPersistent = true;
            break;
          case 403:
            errorTitle = 'Acesso Negado';
            errorMessage = 'Acesso negado. Sua conta pode estar desativada.';
            isPersistent = true;
            break;
          case 404:
            errorTitle = 'Serviço Não Encontrado';
            errorMessage = 'Serviço não encontrado. Entre em contato com o suporte.';
            break;
          case 429:
            errorTitle = 'Muitas Tentativas';
            errorMessage = 'Muitas tentativas de login. Aguarde alguns minutos e tente novamente.';
            isPersistent = true;
            break;
          case 500:
            errorTitle = 'Erro do Servidor';
            errorMessage = 'Erro interno do servidor. Tente novamente mais tarde.';
            isConnectionError = true;
            break;
          case 503:
            errorTitle = 'Serviço Indisponível';
            errorMessage = 'Serviço temporariamente indisponível. Tente novamente em alguns minutos.';
            isConnectionError = true;
            break;
          default:
            errorTitle = 'Erro de Conexão';
            errorMessage = 'Erro de conexão. Verifique sua internet e tente novamente.';
            isConnectionError = true;
        }
      } else if (error.request) {
        // Erro de rede
        errorTitle = 'Sem Conexão';
        errorMessage = 'Sem conexão com o servidor. Verifique sua internet e tente novamente.';
        isConnectionError = true;
        isPersistent = true;
      }

      setErroConexao(isConnectionError);

      // Mostrar alerta modal para erros importantes
      if (isPersistent || errorTitle === 'Credenciais Incorretas' || errorTitle === 'Acesso Negado') {
        showAlert('error', errorTitle, errorMessage, true);
      } else {
        // Para erros menos críticos, usar toast
        toast.error(errorMessage);
      }

      // Limpar apenas a senha em caso de erro, manter o email para facilitar nova tentativa
      setSenha('');
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-gray-800 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Botão Voltar */}
        <Link
          to={empresaId ? `/menu/${empresaId}` : "/"}
          className="inline-flex items-center gap-2 text-gray-400 hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="h-5 w-5" />
          Voltar ao Menu
        </Link>

        {/* Card de Login */}
        <div className="bg-gradient-to-br from-gray-800/90 to-gray-900/90 rounded-3xl p-8 border border-gray-700/50 shadow-2xl backdrop-blur-sm">
          {/* Header */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <User className="h-8 w-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-white mb-2">
              Área do Cliente
            </h1>
            <p className="text-gray-400">
              Faça login para fazer suas compras
            </p>
          </div>

          {/* ── STEP: Usuários Recentes ── */}
          {step === 'recent' && (
            <div>
              <p className="text-center text-gray-400 text-sm mb-4">Selecione seu perfil</p>
              <div className="space-y-2">
                {recentUsers.map(user => (
                  <div
                    key={user.email}
                    onClick={() => handleSelectRecent(user)}
                    className="flex items-center gap-3 p-3 rounded-xl border border-gray-600/30 bg-gray-700/30 hover:bg-gray-700/60 hover:border-orange-500/40 cursor-pointer transition-all duration-200 group"
                  >
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center text-white font-bold text-sm flex-shrink-0 shadow-md"
                      style={{ background: getAvatarColor(user.email) }}
                    >
                      {getInitials(user.nome)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-sm truncate">{user.nome}</div>
                      <div className="text-gray-400 text-xs truncate">{user.email}</div>
                    </div>
                    <button
                      onClick={(e) => removeRecent(e, user.email)}
                      className="text-gray-500 hover:text-red-400 transition-colors text-xl leading-none px-1 opacity-0 group-hover:opacity-100"
                      title="Remover"
                    >×</button>
                  </div>
                ))}
              </div>
              <div className="text-center mt-5">
                <button
                  onClick={() => { setStep('form'); setEmail(''); setSenha(''); }}
                  className="text-orange-400 hover:text-orange-300 text-sm font-medium transition-colors"
                >
                  + Entrar com outro email
                </button>
              </div>
            </div>
          )}

          {/* ── STEP: Formulário Manual ── */}
          {step === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-5">
              {recentUsers.length > 0 && (
                <button
                  type="button"
                  onClick={() => { setStep('recent'); setEmail(''); setSenha(''); }}
                  className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors mb-1"
                >
                  <ArrowLeft className="h-3 w-3" /> Voltar
                </button>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                    placeholder="seu@email.com" autoFocus required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Senha</label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type={mostrarSenha ? 'text' : 'password'} value={senha}
                    onChange={(e) => setSenha(e.target.value)}
                    className="w-full pl-10 pr-12 py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                    placeholder="Sua senha" required
                  />
                  <button type="button" onClick={() => setMostrarSenha(!mostrarSenha)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                    {mostrarSenha ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end text-sm">
                <Link to={empresaId ? `/recuperar-senha/${empresaId}` : "/recuperar-senha"}
                  className="text-orange-400 hover:text-orange-300 transition-colors">Esqueceu a senha?</Link>
              </div>
              {erroConexao && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">
                  <p className="text-red-400 text-sm">⚠️ Problema de conexão detectado. Verifique sua internet.</p>
                </div>
              )}
              <button type="submit" disabled={isLoading}
                className={`w-full py-3 rounded-xl font-bold transition-all duration-300 transform shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${erroConexao ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                    : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 hover:scale-105'
                  } text-white`}>
                {isLoading ? <div className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Entrando...</div> : 'Fazer Login'}
              </button>
            </form>
          )}

          {/* ── STEP: Senha para usuário recente ── */}
          {step === 'password' && selectedUser && (
            <div>
              <button
                onClick={() => { setStep('recent'); setSelectedUser(null); setSenha(''); setErroConexao(false); }}
                className="flex items-center gap-1 text-gray-400 hover:text-white text-sm transition-colors mb-5"
              >
                <ArrowLeft className="h-3 w-3" /> Voltar
              </button>
              <div className="text-center mb-6">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center text-white font-bold text-2xl mx-auto mb-3 shadow-lg"
                  style={{ background: getAvatarColor(selectedUser.email) }}
                >
                  {getInitials(selectedUser.nome)}
                </div>
                <div className="text-white font-bold text-lg">{selectedUser.nome}</div>
                <div className="text-gray-400 text-sm">{selectedUser.email}</div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Senha</label>
                  <div className="relative">
                    <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                    <input
                      type={mostrarSenha ? 'text' : 'password'} value={senha}
                      onChange={(e) => setSenha(e.target.value)}
                      className="w-full pl-10 pr-12 py-3 bg-gray-700/50 border border-gray-600/30 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent transition-all duration-300"
                      placeholder="Sua senha" autoFocus required
                    />
                    <button type="button" onClick={() => setMostrarSenha(!mostrarSenha)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors">
                      {mostrarSenha ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                    </button>
                  </div>
                </div>
                <div className="flex justify-end text-sm">
                  <Link to={empresaId ? `/recuperar-senha/${empresaId}` : "/recuperar-senha"}
                    className="text-orange-400 hover:text-orange-300 transition-colors">Esqueceu a senha?</Link>
                </div>
                {erroConexao && (
                  <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3 text-center">
                    <p className="text-red-400 text-sm">⚠️ Problema de conexão. Verifique sua internet.</p>
                  </div>
                )}
                <button type="submit" disabled={isLoading}
                  className={`w-full py-3 rounded-xl font-bold transition-all duration-300 transform shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none ${erroConexao ? 'bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700'
                      : 'bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 hover:scale-105'
                    } text-white`}>
                  {isLoading ? <div className="flex items-center justify-center gap-2"><div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>Entrando...</div> : 'Entrar'}
                </button>
              </form>
            </div>
          )}



          {/* Divisor */}
          <div className="my-8">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-600/30"></div>
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-gray-800/90 text-gray-400">
                  Não tem uma conta?
                </span>
              </div>
            </div>
          </div>

          {/* Botão de Registro */}
          <Link
            to={empresaId ? `/registro/${empresaId}` : "/registro"}
            className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-xl font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
          >
            Criar Conta
          </Link>
        </div>

        {/* Footer */}
        <div className="text-center mt-8 space-y-4">
          <p className="text-gray-500 text-sm">
            © 2025 DK SYSTEMS. Todos os direitos reservados.
          </p>
          <div className="flex justify-center gap-4">
            <Link 
              to={empresaId ? `/politica-privacidade/${empresaId}` : "/politica-privacidade"}
              className="text-gray-500 hover:text-orange-400 text-xs transition-colors underline decoration-gray-700 underline-offset-4"
            >
              Política de Privacidade
            </Link>
          </div>
        </div>
      </div>

      {/* Modal de Alerta */}
      {alertModal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl transform transition-all duration-300 scale-100 animate-in fade-in-0 zoom-in-95">
            {/* Header do Modal */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-3">
                {alertModal.type === 'error' && (
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-red-600" />
                  </div>
                )}
                {alertModal.type === 'success' && (
                  <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  </div>
                )}
                {alertModal.type === 'warning' && (
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center">
                    <AlertCircle className="w-6 h-6 text-yellow-600" />
                  </div>
                )}
                <h3 className="text-lg font-bold text-gray-900">
                  {alertModal.title}
                </h3>
              </div>
              {!alertModal.persistent && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    closeAlert();
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              )}
            </div>

            {/* Conteúdo do Modal */}
            <div className="mb-6">
              <p className="text-gray-700 leading-relaxed">
                {alertModal.message}
              </p>
            </div>

            {/* Botões do Modal */}
            <div className="flex gap-3">
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  closeAlert();
                }}
                className={`flex-1 py-3 px-4 rounded-xl font-bold transition-all duration-300 ${alertModal.type === 'error'
                    ? 'bg-red-600 hover:bg-red-700 text-white'
                    : alertModal.type === 'success'
                      ? 'bg-green-600 hover:bg-green-700 text-white'
                      : 'bg-yellow-600 hover:bg-yellow-700 text-white'
                  }`}
              >
                OK
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Escolha de Destino para Usuários com Cargo */}
      {showEscolhaDestino && usuarioLogado && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999] p-4" style={{ animation: 'fadeIn 0.3s ease-out' }}>
          <div className="bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl w-full max-w-md border border-gray-600/50 shadow-2xl">
            <div className="p-6">
              {/* Header */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-white">
                    Login Realizado!
                  </h3>
                  <p className="text-sm text-gray-400">
                    Escolha seu destino
                  </p>
                </div>
              </div>

              {/* Conteúdo */}
              <div className="mb-6">
                <p className="text-white mb-4">
                  Olá, <span className="font-bold text-orange-400">{usuarioLogado.nome}</span>!
                </p>
                <p className="text-gray-300 mb-4">
                  Você possui um cargo na empresa ({usuarioLogado.role || usuarioLogado.nivel}).
                  Escolha para onde deseja ir:
                </p>

                <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-4 mb-4">
                  <div className="space-y-2">
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-blue-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-blue-300 text-sm">
                        <span className="font-semibold">Dashboard:</span> Área administrativa para gerenciar produtos, pedidos e configurações
                      </span>
                    </div>
                    <div className="flex items-start gap-2">
                      <div className="w-2 h-2 bg-green-400 rounded-full mt-2 flex-shrink-0"></div>
                      <span className="text-green-300 text-sm">
                        <span className="font-semibold">Página de Clientes:</span> Visualizar o menu público como um cliente
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Botões */}
              <div className="flex flex-col gap-3">
                <button
                  onClick={() => {
                    setShowEscolhaDestino(false);
                    navigate('/dashboard');
                  }}
                  className="w-full bg-gradient-to-r from-blue-500 to-purple-500 text-white py-3 rounded-xl font-bold hover:from-blue-600 hover:to-purple-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <LayoutDashboard className="h-5 w-5" />
                  Ir para Dashboard
                </button>
                <button
                  onClick={() => {
                    setShowEscolhaDestino(false);
                    if (empresaId) {
                      navigate(`/menu/${empresaId}`);
                    } else if (usuarioLogado?.empresa_id) {
                      navigate(`/menu/${usuarioLogado.empresa_id}`);
                    } else {
                      navigate('/');
                    }
                  }}
                  className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white py-3 rounded-xl font-bold hover:from-green-600 hover:to-emerald-600 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl flex items-center justify-center gap-2"
                >
                  <ShoppingBag className="h-5 w-5" />
                  Ir para Página de Clientes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      <style>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
